"use client"

import { useEffect, useState, useCallback } from "react"
import { toast } from "sonner"
import {
  Activity,
  Clock,
  MessageSquare,
  FileText,
  Phone,
  Send,
  Zap,
  CheckCircle2,
  Bell,
  Pencil,
  Plus,
  Save,
  X,
  ArrowRightLeft,
} from "@/lib/icons"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  getLeadActivities,
  logLeadActivity,
  deleteLeadActivity,
  getSupabase,
} from "@/lib/supabase"

const TIPO_META = {
  mensaje_enviado: { icon: Send, color: "#0369a1", bg: "rgba(14,165,233,0.18)", label: "Mensaje" },
  documento_enviado: { icon: FileText, color: "#4338ca", bg: "rgba(99,102,241,0.18)", label: "Documento" },
  respuesta_rapida: { icon: Zap, color: "#7e22ce", bg: "rgba(168,85,247,0.18)", label: "Respuesta rápida" },
  llamada: { icon: Phone, color: "#15803d", bg: "rgba(34,197,94,0.18)", label: "Llamada" },
  cambio_estado: { icon: CheckCircle2, color: "#b45309", bg: "rgba(245,158,11,0.2)", label: "Cambio de estado" },
  nota: { icon: Pencil, color: "#334155", bg: "rgba(100,116,139,0.18)", label: "Nota" },
  cita_agendada: { icon: Clock, color: "#7a1b33", bg: "rgba(122,27,51,0.18)", label: "Cita" },
  recordatorio: { icon: Bell, color: "#be123c", bg: "rgba(244,63,94,0.18)", label: "Recordatorio" },
  cross_instance_notice: { icon: MessageSquare, color: "#7c3aed", bg: "rgba(124,58,237,0.18)", label: "Aviso cross-instance" },
  transfer_requested: { icon: ArrowRightLeft, color: "#4f46e5", bg: "rgba(79,70,229,0.18)", label: "Transferencia solicitada" },
  transfer_accepted: { icon: CheckCircle2, color: "#059669", bg: "rgba(5,150,105,0.18)", label: "Transferencia aceptada" },
  transfer_rejected: { icon: X, color: "#dc2626", bg: "rgba(220,38,38,0.18)", label: "Transferencia rechazada" },
  otro: { icon: Activity, color: "#475569", bg: "rgba(148,163,184,0.2)", label: "Actividad" },
}

const TIPO_MANUAL_OPTIONS = [
  { value: "llamada", label: "Llamada realizada" },
  { value: "nota", label: "Nota general" },
  { value: "otro", label: "Otra actividad" },
]

function formatDate(iso) {
  if (!iso) return ""
  const d = new Date(iso)
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  const isYest = d.toDateString() === yesterday.toDateString()
  const hm = d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })
  if (sameDay) return `Hoy · ${hm}`
  if (isYest) return `Ayer · ${hm}`
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
}

const INITIAL_VISIBLE_COUNT = 3

export function LeadActivityTimeline({ leadId, asesorId = null, reloadKey = 0 }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [draft, setDraft] = useState({ tipo: "llamada", titulo: "", descripcion: "" })
  const [saving, setSaving] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const reload = useCallback(async () => {
    if (!leadId) return
    setLoading(true)
    const rows = await getLeadActivities(leadId, 80)
    setItems(rows)
    setLoading(false)
  }, [leadId])

  useEffect(() => { reload() }, [reload, reloadKey])

  // Realtime: si otra CAP registra una actividad en este lead, el timeline
  // se refresca solo (antes requería cerrar y reabrir el chat)
  useEffect(() => {
    if (!leadId) return
    const channel = getSupabase()
      .channel(`lead-activities-${leadId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "lead_activities", filter: `lead_id=eq.${leadId}` },
        () => reload(),
      )
      .subscribe()
    return () => { getSupabase().removeChannel(channel) }
  }, [leadId, reload])

  const handleAdd = async () => {
    if (!draft.titulo.trim()) {
      toast.error("El título es obligatorio")
      return
    }
    setSaving(true)
    const { error } = await logLeadActivity(leadId, {
      tipo: draft.tipo,
      titulo: draft.titulo.trim(),
      descripcion: draft.descripcion.trim() || null,
      asesor_id: asesorId,
    })
    setSaving(false)
    if (error) {
      toast.error("No se pudo registrar", { description: error.message })
      return
    }
    setDraft({ tipo: "llamada", titulo: "", descripcion: "" })
    setShowAdd(false)
    toast.success("Actividad registrada")
    reload()
  }

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta entrada del historial?")) return
    const { error } = await deleteLeadActivity(id)
    if (error) {
      toast.error("No se pudo eliminar")
      return
    }
    reload()
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
          <Activity className="h-3 w-3" /> Historial de actividad
        </p>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="text-[10px] font-semibold text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 px-2 py-0.5 rounded-full hover:bg-rose-100 dark:hover:bg-rose-950/60 transition-colors flex items-center gap-1"
        >
          {showAdd ? <X className="h-2.5 w-2.5" /> : <Plus className="h-2.5 w-2.5" />}
          {showAdd ? "Cancelar" : "Agregar"}
        </button>
      </div>

      {showAdd && (
        <div className="rounded-lg border border-rose-200 dark:border-rose-900/40 bg-rose-50/40 dark:bg-rose-950/20 p-2.5 space-y-2">
          <select
            value={draft.tipo}
            onChange={(e) => setDraft((d) => ({ ...d, tipo: e.target.value }))}
            className="h-8 w-full rounded-lg border border-slate-200/80 dark:border-slate-700/80 bg-white dark:bg-slate-900 px-2 text-xs"
          >
            {TIPO_MANUAL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <Input
            value={draft.titulo}
            onChange={(e) => setDraft((d) => ({ ...d, titulo: e.target.value }))}
            placeholder="Título (ej: Le llamé y contestó)"
            className="h-8 text-xs"
          />
          <Textarea
            value={draft.descripcion}
            onChange={(e) => setDraft((d) => ({ ...d, descripcion: e.target.value }))}
            placeholder="Detalle (opcional)"
            className="min-h-[50px] text-xs resize-none"
          />
          <Button
            type="button"
            size="sm"
            className="w-full h-8 text-[11px] bg-rose-600 hover:bg-rose-700 text-white"
            disabled={saving || !draft.titulo.trim()}
            onClick={handleAdd}
          >
            <Save className="h-3 w-3 mr-1" />
            {saving ? "Guardando..." : "Registrar actividad"}
          </Button>
        </div>
      )}

      {loading && <p className="text-[11px] text-slate-500 dark:text-slate-400">Cargando…</p>}

      {!loading && items.length === 0 && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
          Aún no hay actividad registrada para este prospecto.
        </p>
      )}

      {(() => {
        const visibleItems = expanded ? items : items.slice(0, INITIAL_VISIBLE_COUNT)
        const hiddenCount = items.length - INITIAL_VISIBLE_COUNT
        return (
          <>
            <div className="relative space-y-0">
              {visibleItems.map((item, idx) => {
                const meta = TIPO_META[item.tipo] || TIPO_META.otro
                const Icon = meta.icon
                const isLast = idx === visibleItems.length - 1 && (!expanded || idx === items.length - 1)
                const canDelete = item.tipo === "llamada" || item.tipo === "nota" || item.tipo === "otro"
                return (
                  <div key={item.id} className="relative flex gap-2.5 pb-2.5">
                    {!isLast && (
                      <div
                        className="absolute left-[11px] top-6 bottom-0 w-px bg-slate-200 dark:bg-slate-800"
                        aria-hidden
                      />
                    )}
                    <div
                      className="flex-shrink-0 h-[22px] w-[22px] rounded-full flex items-center justify-center relative z-10 ring-2 ring-white dark:ring-slate-900 shadow-sm"
                      style={{ backgroundColor: meta.bg, color: meta.color }}
                    >
                      <Icon className="h-3 w-3" strokeWidth={2.5} />
                    </div>
                    <div className="flex-1 min-w-0 pt-0.5 group">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[11.5px] font-semibold leading-tight truncate text-slate-900 dark:text-slate-50">
                          {item.titulo}
                        </p>
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-red-600 transition-all"
                            title="Eliminar"
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        )}
                      </div>
                      {item.descripcion && (
                        <p className="text-[10.5px] text-slate-600 dark:text-slate-300 leading-snug mt-0.5 line-clamp-2">
                          {item.descripcion}
                        </p>
                      )}
                      <p className="text-[9.5px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1 font-medium">
                        <Clock className="h-2 w-2" />
                        {formatDate(item.created_at)}
                        {item.asesor?.nombre && <span>· {item.asesor.nombre}</span>}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>

            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full text-[10.5px] font-semibold text-rose-700 dark:text-rose-300 bg-rose-50/60 dark:bg-rose-950/20 hover:bg-rose-100/80 dark:hover:bg-rose-950/40 rounded-lg py-1.5 border border-rose-200/50 dark:border-rose-900/30 transition-colors"
              >
                {expanded
                  ? "Ocultar actividades antiguas"
                  : `Ver ${hiddenCount} actividad${hiddenCount !== 1 ? "es" : ""} más`}
              </button>
            )}
          </>
        )
      })()}
    </div>
  )
}
